import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import Toast from "../../../domains/Utilities/Toast";

type Filter = {
  country: string
  sites: string[]
  component: string
  product: string
  port: { name: string; id: string }
  rangeLimit: number
  startDate: Date | string
  endDate: Date | string
}

export interface CapabilityAnalysisGraphData {
  sampleAmount: number[]
  probabilities: number[]
  probabilityPlotValues: number[]
  sampleMean: number
}

interface AnalysisObservations {
  title: string
  value: string
}

interface AnalysisExplanations {
  outlierDetection: {
    IQRText: string
    grubbsText: string
  }
  normalityText: string
}

interface CapabilityAnalysisState {
  filter: Filter | null
  graphData: CapabilityAnalysisGraphData
  analysisObservations: AnalysisObservations[]
  expectedOverallPerformance: number
  observedPerformance: number
  analysisExplanations: AnalysisExplanations
  isCapabilityAnalysisDataLoading: boolean
  hasErrorGettingAnalysis: boolean
  errorMessage: string
  analysisToast: Toast;
}

const initialState: CapabilityAnalysisState = {
  isCapabilityAnalysisDataLoading: false,
  analysisToast: {message: "", isOpen: false, status: undefined},
  graphData: undefined,
  analysisObservations: [],
  expectedOverallPerformance: 0,
  observedPerformance: 0,
  analysisExplanations: undefined,
  hasErrorGettingAnalysis: false,
  errorMessage: "",
  filter: {
    country: "",
    sites: [],
    component: "",
    product: "",
    port: { name: "", id: "" },
    rangeLimit: 0,
    startDate: "",
    endDate: "",
  },
};

export const SLICE_NAME = "capabilityAnalysis";

export const capabilityAnalysisSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setFilterValue: (
      state,
      action: PayloadAction<{ field: string; value: string }>,
    ) => {
      const field = action.payload.field;
      const value = action.payload.value;
      state[field] = value;
    },
    setGraphData: (
      state,
      action: PayloadAction<CapabilityAnalysisGraphData>,
    ) => {
      state.graphData = action.payload;
    },
    resetGraphData: state => {
      state.graphData = undefined;
    },
    setAnalysisObservations: (
      state,
      action: PayloadAction<AnalysisObservations[]>,
    ) => {
      state.analysisObservations = [...action.payload];
    },
    resetAnalysisObservations: state => {
      state.analysisObservations = [];
    },
    setAnalysisExplanations: (
      state,
      action: PayloadAction<AnalysisExplanations>,
    ) => {
      state.analysisExplanations = action.payload;
    },
    resetAnalysisExplanations: state => {
      state.analysisExplanations = undefined;
    },
    setPerformance: (
      state,
      action: PayloadAction<{
        expectedOverallPerformance: number
        observedPerformance: number
      }>,
    ) => {
      state.expectedOverallPerformance =
        action.payload.expectedOverallPerformance;
      state.observedPerformance = action.payload.observedPerformance;
    },
    setAnalysisLoading: (state, action: PayloadAction<boolean>) => {
      state.isCapabilityAnalysisDataLoading = action.payload;
    },
    setErrorGettingAnalysis: (state, action: PayloadAction<{hasError: boolean, errorMessage: string}>) => {
      state.hasErrorGettingAnalysis = action.payload.hasError;
      state.errorMessage = action.payload.errorMessage;
    },
    openAnalysisToast: (
      state: CapabilityAnalysisState,
      action: PayloadAction<{
        status: "success" | "info" | "error" | undefined;
        message?: string;
      }>
    ) => {
      state.analysisToast.status = action.payload.status;
      state.analysisToast.message = action.payload.message;
      state.analysisToast.isOpen = true;
    },
    closeAnalysisToast: (state: CapabilityAnalysisState) => {
      state.analysisToast.isOpen = false;
      state.analysisToast.status = undefined;
      state.analysisToast.message = "";
    },
  },
});

export const {
  setFilterValue,
  setGraphData,
  resetGraphData,
  setAnalysisExplanations,
  resetAnalysisExplanations,
  setAnalysisObservations,
  resetAnalysisObservations,
  setPerformance,
  setAnalysisLoading,
  setErrorGettingAnalysis,
  openAnalysisToast,
  closeAnalysisToast
} = capabilityAnalysisSlice.actions;

export default capabilityAnalysisSlice.reducer;
